import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Clock,
  Check,
  X,
  Calendar,
  Cog,
  MoreVertical,
  Pencil,
  Trash2,
  CalendarClock,
  CheckCircle2,
  Loader2,
  ClipboardCheck,
  List,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700", dotColor: "bg-amber-500" },
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700", dotColor: "bg-blue-500" },
  confirmed: { label: "Confirmed", color: "bg-emerald-100 text-emerald-700", dotColor: "bg-emerald-500" },
  completed: { label: "Completed", color: "bg-slate-100 text-slate-600", dotColor: "bg-slate-400" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-600", dotColor: "bg-red-400" },
  overdue: { label: "Overdue", color: "bg-red-100 text-red-700", dotColor: "bg-red-500" },
};

const CustomerServices = ({ customerId, customerName, installations }) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [installationFilter, setInstallationFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list"); // "list" or "timeline"
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Single booking actions
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    booking_date: "",
    start_time: "",
    end_time: "",
  });
  
  // Bulk action state
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  
  // Timeline state
  const [timelineDate, setTimelineDate] = useState(new Date());

  const fetchData = async () => {
    try {
      const [bookingsRes, usersRes] = await Promise.all([
        axios.get(`${API}/service-bookings`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
      ]);
      
      // Filter for this customer's bookings
      const customerBookings = (bookingsRes.data || [])
        .filter(b => b.customer_id === customerId)
        .sort((a, b) => b.booking_date.localeCompare(a.booking_date));
      
      setBookings(customerBookings);
      setWorkers(usersRes.data.filter(u => u.role === "worker" || u.role === "manager"));
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchData();
    }
  }, [customerId]);

  // Filter bookings
  const filteredBookings = useMemo(() => {
    return bookings.filter(b => {
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (installationFilter !== "all" && b.installation_id !== installationFilter) return false;
      return true;
    });
  }, [bookings, statusFilter, installationFilter]);

  // Group bookings by installation
  const groupedByInstallation = useMemo(() => {
    const groups = {};
    filteredBookings.forEach(b => {
      const key = b.installation_id || "unknown";
      if (!groups[key]) {
        groups[key] = {
          installation_id: b.installation_id,
          installation_name: b.installation_name || "Unknown Installation",
          bookings: [],
        };
      }
      groups[key].bookings.push(b);
    });
    return Object.values(groups);
  }, [filteredBookings]);

  // Timeline data - bookings for current month
  const timelineBookings = useMemo(() => {
    const year = timelineDate.getFullYear();
    const month = timelineDate.getMonth();
    return filteredBookings.filter(b => {
      const d = new Date(b.booking_date);
      return d.getFullYear() === year && d.getMonth() === month;
    });
  }, [filteredBookings, timelineDate]);

  const handleAction = async (bookingId, action) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/service-bookings/${bookingId}/${action}`,
        {},
        { withCredentials: true }
      );
      toast.success(
        action === "confirm" ? "Booking confirmed" :
        action === "complete" ? "Booking completed" : "Booking cancelled"
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} booking`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedBooking || !rescheduleData.booking_date || !rescheduleData.start_time || !rescheduleData.end_time) {
      toast.error("Please fill in all reschedule fields");
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/service-bookings/${selectedBooking.booking_id}/reschedule`,
        {
          booking_date: rescheduleData.booking_date,
          time_slot: {
            start_time: rescheduleData.start_time,
            end_time: rescheduleData.end_time
          }
        },
        { withCredentials: true }
      );
      toast.success("Booking rescheduled");
      setRescheduleDialogOpen(false);
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reschedule booking");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBooking) return;
    setActionLoading(true);
    try {
      await axios.delete(
        `${API}/service-bookings/${selectedBooking.booking_id}`,
        { withCredentials: true }
      );
      toast.success("Booking deleted");
      setDeleteDialogOpen(false);
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete booking");
    } finally {
      setActionLoading(false);
    }
  };

  const openRescheduleDialog = (booking) => {
    setSelectedBooking(booking);
    setRescheduleData({
      booking_date: booking.booking_date || "",
      start_time: booking.time_slot?.start_time || "",
      end_time: booking.time_slot?.end_time || "",
    });
    setRescheduleDialogOpen(true);
  };

  const openDeleteDialog = (booking) => {
    setSelectedBooking(booking);
    setDeleteDialogOpen(true);
  };

  // Bulk actions
  const toggleSelectBooking = (bookingId) => {
    setSelectedBookings(prev =>
      prev.includes(bookingId)
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedBookings.length === filteredBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings.map(b => b.booking_id));
    }
  };

  const handleBulkAction = async () => {
    if (selectedBookings.length === 0) return;
    
    setActionLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const bookingId of selectedBookings) {
      try {
        await axios.post(
          `${API}/service-bookings/${bookingId}/${bulkAction}`,
          {},
          { withCredentials: true }
        );
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }

    setBulkActionDialogOpen(false);
    setSelectedBookings([]);
    
    if (successCount > 0) {
      toast.success(`${successCount} booking(s) processed`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to process ${errorCount} booking(s)`);
    }
    
    setActionLoading(false);
    fetchData();
  };

  // Calendar helpers
  const getDaysInMonth = () => {
    const year = timelineDate.getFullYear();
    const month = timelineDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getBookingsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${timelineDate.getFullYear()}-${String(timelineDate.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return timelineBookings.filter(b => b.booking_date === dateStr);
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-slate-200 rounded w-48"></div>
        <div className="h-64 bg-slate-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="customer-services">
      {/* Header with filters */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">
            {filteredBookings.length} service{filteredBookings.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 h-8 text-xs rounded-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Installation Filter */}
          {installations && installations.length > 1 && (
            <Select value={installationFilter} onValueChange={setInstallationFilter}>
              <SelectTrigger className="w-40 h-8 text-xs rounded-sm">
                <SelectValue placeholder="Installation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Installations</SelectItem>
                {installations.map(inst => (
                  <SelectItem key={inst.installation_id} value={inst.installation_id}>
                    {inst.system_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* View Toggle */}
          <div className="flex border rounded-sm">
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="sm"
              className="h-8 rounded-none rounded-l-sm"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "timeline" ? "default" : "ghost"}
              size="sm"
              className="h-8 rounded-none rounded-r-sm"
              onClick={() => setViewMode("timeline")}
            >
              <CalendarDays className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedBookings.length > 0 && (
        <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-sm">
          <span className="text-sm text-slate-600">{selectedBookings.length} selected</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setBulkAction("confirm");
              setBulkActionDialogOpen(true);
            }}
            className="h-7 text-xs"
          >
            <Check className="h-3 w-3 mr-1" />
            Confirm
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setBulkAction("complete");
              setBulkActionDialogOpen(true);
            }}
            className="h-7 text-xs"
          >
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Complete
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setBulkAction("cancel");
              setBulkActionDialogOpen(true);
            }}
            className="h-7 text-xs text-red-600"
          >
            <X className="h-3 w-3 mr-1" />
            Cancel
          </Button>
        </div>
      )}

      {/* Content */}
      {viewMode === "list" ? (
        // List View - Grouped by Installation
        <div className="space-y-6">
          {groupedByInstallation.length > 0 ? (
            groupedByInstallation.map(group => (
              <Card key={group.installation_id} className="rounded-sm">
                <CardHeader className="py-3 px-4 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Cog className="h-4 w-4 text-slate-500" />
                    <CardTitle className="text-sm font-medium">{group.installation_name}</CardTitle>
                    <Badge variant="outline" className="text-xs">{group.bookings.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={group.bookings.every(b => selectedBookings.includes(b.booking_id))}
                            onCheckedChange={() => {
                              const groupIds = group.bookings.map(b => b.booking_id);
                              const allSelected = groupIds.every(id => selectedBookings.includes(id));
                              if (allSelected) {
                                setSelectedBookings(prev => prev.filter(id => !groupIds.includes(id)));
                              } else {
                                setSelectedBookings(prev => [...new Set([...prev, ...groupIds])]);
                              }
                            }}
                          />
                        </TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Technician</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.bookings.map(booking => (
                        <TableRow key={booking.booking_id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedBookings.includes(booking.booking_id)}
                              onCheckedChange={() => toggleSelectBooking(booking.booking_id)}
                            />
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{booking.service_name || "General Service"}</span>
                          </TableCell>
                          <TableCell>{booking.booking_date}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {booking.time_slot?.start_time} - {booking.time_slot?.end_time}
                          </TableCell>
                          <TableCell>
                            <Badge className={`rounded-sm ${statusConfig[booking.status]?.color || "bg-slate-100"}`}>
                              {statusConfig[booking.status]?.label || booking.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {booking.assigned_worker_name || <span className="text-slate-400">—</span>}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {["confirmed", "scheduled"].includes(booking.status) && (
                                  <DropdownMenuItem onClick={() => navigate(`/job-card/${booking.booking_id}`)}>
                                    <ClipboardCheck className="mr-2 h-4 w-4" />
                                    View Job Card
                                  </DropdownMenuItem>
                                )}
                                {booking.status === "pending" && (
                                  <DropdownMenuItem onClick={() => handleAction(booking.booking_id, "confirm")}>
                                    <Check className="mr-2 h-4 w-4" />
                                    Confirm
                                  </DropdownMenuItem>
                                )}
                                {["confirmed", "scheduled"].includes(booking.status) && (
                                  <DropdownMenuItem onClick={() => handleAction(booking.booking_id, "complete")}>
                                    <CheckCircle2 className="mr-2 h-4 w-4" />
                                    Mark Complete
                                  </DropdownMenuItem>
                                )}
                                {!["completed", "cancelled"].includes(booking.status) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => openRescheduleDialog(booking)}>
                                      <CalendarClock className="mr-2 h-4 w-4" />
                                      Reschedule
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => openDeleteDialog(booking)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No services found</p>
              <p className="text-sm text-slate-400 mt-1">
                {statusFilter !== "all" || installationFilter !== "all"
                  ? "Try adjusting your filters"
                  : "No service bookings for this customer yet"}
              </p>
            </div>
          )}
        </div>
      ) : (
        // Timeline/Calendar View
        <Card className="rounded-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setTimelineDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                className="rounded-sm h-8 w-8"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h3 className="font-semibold">
                {monthNames[timelineDate.getMonth()]} {timelineDate.getFullYear()}
              </h3>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setTimelineDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                className="rounded-sm h-8 w-8"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth().map((day, index) => {
                const dayBookings = getBookingsForDay(day);
                const isToday = day && new Date().toDateString() === new Date(timelineDate.getFullYear(), timelineDate.getMonth(), day).toDateString();
                
                return (
                  <div
                    key={`${timelineDate.getFullYear()}-${timelineDate.getMonth()}-${day ?? `pad-${index}`}`}
                    className={`min-h-[80px] p-1 border rounded-sm ${
                      day ? "bg-white" : "bg-slate-50"
                    } ${isToday ? "border-blue-500 border-2" : "border-slate-200"}`}
                  >
                    {day && (
                      <>
                        <div className={`text-xs font-medium mb-1 ${isToday ? "text-blue-600" : "text-slate-600"}`}>
                          {day}
                        </div>
                        <div className="space-y-1">
                          {dayBookings.slice(0, 2).map(booking => (
                            <div
                              key={booking.booking_id}
                              className="flex items-center gap-1 text-xs p-1 rounded cursor-pointer hover:bg-slate-100"
                              onClick={() => navigate(`/job-card/${booking.booking_id}`)}
                              title={`${booking.service_name} - ${statusConfig[booking.status]?.label}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusConfig[booking.status]?.dotColor || "bg-slate-400"}`}></span>
                              <span className="truncate">{booking.service_name || "Service"}</span>
                            </div>
                          ))}
                          {dayBookings.length > 2 && (
                            <div className="text-xs text-slate-500 pl-1">+{dayBookings.length - 2} more</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
              {Object.entries(statusConfig).slice(0, 5).map(([key, config]) => (
                <div key={key} className="flex items-center gap-1.5 text-xs">
                  <span className={`w-2 h-2 rounded-full ${config.dotColor}`}></span>
                  <span className="text-slate-600">{config.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule Service</DialogTitle>
            <DialogDescription>
              Select a new date and time. The customer will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>New Date</Label>
              <Input
                type="date"
                value={rescheduleData.booking_date}
                onChange={(e) => setRescheduleData({ ...rescheduleData, booking_date: e.target.value })}
                className="rounded-sm mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={rescheduleData.start_time}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, start_time: e.target.value })}
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={rescheduleData.end_time}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, end_time: e.target.value })}
                  className="rounded-sm mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleReschedule} disabled={actionLoading} className="rounded-sm">
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />}
              Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Service Booking</DialogTitle>
            <DialogDescription>
              This action cannot be undone. The customer will be notified.
            </DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                <p className="text-sm font-medium text-red-800">{selectedBooking.service_name || "Service"}</p>
                <p className="text-sm text-red-600">
                  {selectedBooking.booking_date} at {selectedBooking.time_slot?.start_time}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading} className="rounded-sm">
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === "confirm" ? "Confirm Selected" :
               bulkAction === "complete" ? "Complete Selected" : "Cancel Selected"}
            </DialogTitle>
            <DialogDescription>
              This will apply to {selectedBookings.length} booking(s).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              onClick={handleBulkAction}
              disabled={actionLoading}
              className={`rounded-sm ${bulkAction === "cancel" ? "bg-red-600 hover:bg-red-700" : ""}`}
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {bulkAction === "confirm" ? "Confirm All" :
               bulkAction === "complete" ? "Complete All" : "Cancel All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerServices;
